import {
  Box,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Input,
  Select,
  Textarea,
  VStack,
  HStack,
  Icon,
  Text,
  useToast,
  Divider,
  Badge,
  Alert,
  AlertIcon,
  Spinner,
  Center,
  Wrap,
  WrapItem,
  Tag,
  TagLabel,
  TagCloseButton,
  Tooltip,
  IconButton,
} from '@chakra-ui/react'
import { LuPlus, LuShare2, LuUserPlus, LuUsers } from 'react-icons/lu'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useShareBlotCase, type ShareRole } from '../../api/api_hooks/lab/shareBlotCase'
import { useBlotCaseShares, type BlotCaseShare } from '../../api/api_hooks/lab/blotCaseShares'
import { useMyCollaborators } from '../../api/api_hooks/lab/myCollaborators'
import { useMyTeam } from '../../api/api_hooks/lab/myTeam'

interface CollaborationModalProps {
  isOpen: boolean
  onClose: () => void
  /** The case / diagnostic ID to share */
  caseId: string
  /** Display name of the case for context */
  caseName?: string
}

type Role = ShareRole

// Common lab job titles for a collaborateur — a biologist, a technician and
// an intern aren't interchangeable context for the chef reviewing who they
// invited. "Autre" keeps a free-text escape hatch for anything not listed.
const JOB_TITLE_PRESETS = [
  'Technicien.ne de laboratoire',
  'Interne',
  'Biologiste médical',
  'Médecin',
  'Secrétaire médical.e',
] as const
const JOB_TITLE_OTHER = 'Autre'

// One invitation in the making — each recipient carries its own role and
// job title, so a single send can mix lecteurs and collaborateurs.
// `locked` = the role AND job title are already known (an existing
// collaborateur's), so the picker is replaced by static text instead of
// asking the chef/teammate to re-enter something that's already on file.
type Recipient = { email: string; role: Role; preset: string; other: string; locked?: boolean }

const resolveJobTitle = (r: Pick<Recipient, 'preset' | 'other'>) =>
  r.preset === JOB_TITLE_OTHER ? r.other.trim() : r.preset

const presetFromTitle = (title: string | null | undefined): Pick<Recipient, 'preset' | 'other'> => {
  if (!title) return { preset: JOB_TITLE_PRESETS[0], other: '' }
  return (JOB_TITLE_PRESETS as readonly string[]).includes(title)
    ? { preset: title, other: '' }
    : { preset: JOB_TITLE_OTHER, other: title }
}

// A chef's team is capped at this many collaborateurs (the chef is not counted).
const MAX_COLLABORATORS = 2

const validateEmail = (val: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val)

// Addresses typed or pasted in the input field, split on commas, semicolons
// or spaces, lowercased and de-duplicated.
const parseEmails = (raw: string) =>
  Array.from(new Set(raw.split(/[\s,;]+/).map((e) => e.trim().toLowerCase()).filter(Boolean)))

/**
 * Modal to invite one or several people to collaborate on a case.
 * Each recipient is added as a tag and gets its own role (lecteur /
 * collaborateur) and, for a collaborateur, its own job title.
 */
export const CollaborationModal: React.FC<CollaborationModalProps> = ({
  isOpen,
  onClose,
  caseId,
  caseName,
}) => {
  const { t } = useTranslation()
  const toast = useToast()
  const { mutateAsync: shareBlotCase, isPending } = useShareBlotCase()
  const { data: existingShares, isLoading: loadingShares } = useBlotCaseShares(caseId, isOpen)
  // Active collaborateurs from ANY of the chef's cases — lets this modal
  // reuse one on a DIFFERENT case without retyping. existingShares (above)
  // can't serve this: it's scoped to this one case only.
  const { data: myCollaborators } = useMyCollaborators(isOpen)
  // A collaborateur (not a chef) can still share this case with their
  // existing team — the chef, or another of the chef's collaborateurs —
  // but can't add anyone new (see POST /case/:id/share). null for a chef
  // or standalone user, who have no such restriction.
  const { data: myTeam } = useMyTeam(isOpen)
  const isCollaborator = !!myTeam?.isCollaborator
  const teamAllowedEmails = useMemo(() => {
    if (!isCollaborator || !myTeam?.chef) return null
    return new Set([
      myTeam.chef.email.toLowerCase(),
      ...(myTeam.members ?? []).map((m) => m.email.toLowerCase()),
    ])
  }, [isCollaborator, myTeam])
  const teammates = useMemo(() => {
    if (!myTeam?.chef) return []
    const chefEntry = { email: myTeam.chef.email, jobTitle: null, isMe: false }
    return [chefEntry, ...(myTeam.members ?? [])].filter((m) => !m.isMe)
  }, [myTeam])

  // An existing collaborateur's role is always 'editor' and their job title
  // is already on file — prefill both and lock the picker. The chef has
  // neither (they're the case owner, not a share), so 'viewer' with no
  // locked title, same as any generic recipient.
  const knownRecipientDefaults = (email: string): Pick<Recipient, 'role' | 'preset' | 'other' | 'locked'> => {
    if (myTeam?.chef && email === myTeam.chef.email.toLowerCase()) {
      return { role: 'viewer', ...presetFromTitle(null), locked: false }
    }
    const member = myTeam?.members?.find((m) => m.email.toLowerCase() === email)
    if (member) return { role: 'editor', ...presetFromTitle(member.jobTitle), locked: true }
    return { role: 'viewer', ...presetFromTitle(null), locked: false }
  }

  const [input, setInput] = useState('')
  const [inputError, setInputError] = useState('')
  const [recipients, setRecipients] = useState<Recipient[]>([])
  const [message, setMessage] = useState('')
  const [confirmedResend, setConfirmedResend] = useState(false)

  // Recipients whose address already has a share on this case. Resending
  // replaces their token — any link they still have stops working — so
  // warn first, once, before sending.
  const sharedOnThisCase = (list: Recipient[]) => {
    const byEmail = new Map((existingShares ?? []).map((s) => [s.receiverEmail.toLowerCase(), s]))
    return list
      .map((r) => ({ recipient: r, share: byEmail.get(r.email) }))
      .filter((x): x is { recipient: Recipient; share: BlotCaseShare } => !!x.share)
  }
  const alreadyShared = sharedOnThisCase(recipients)

  // Active collaborateurs not already on THIS case — suggested as one-click
  // chips so the list doesn't just repeat "Déjà invités" below.
  const reusableCollaborators = useMemo(() => {
    if (!myCollaborators) return []
    const onThisCase = new Set((existingShares ?? []).map((s) => s.receiverEmail.toLowerCase()))
    return myCollaborators.filter((c) => c.status === 'accepted' && !onThisCase.has(c.email.toLowerCase()))
  }, [myCollaborators, existingShares])

  const addRecipient = (r: Recipient) => {
    setRecipients((list) => (list.some((x) => x.email === r.email) ? list : [...list, r]))
    setConfirmedResend(false)
  }

  // Addresses typed in the field that are not yet tags, as new recipients.
  // For a collaborateur, an address outside the existing team is rejected
  // separately from a malformed one — different message, same "stays in
  // the field to fix" behaviour.
  const pendingFromInput = (): { fresh: Recipient[]; invalid: string[]; notInTeam: string[] } => {
    const addresses = parseEmails(input)
    const invalid = addresses.filter((a) => !validateEmail(a))
    const validFormat = addresses.filter((a) => validateEmail(a))
    const notInTeam = teamAllowedEmails ? validFormat.filter((a) => !teamAllowedEmails.has(a)) : []
    const fresh = validFormat
      .filter((a) => !notInTeam.includes(a) && !recipients.some((r) => r.email === a))
      .map((email) => ({ email, ...knownRecipientDefaults(email) }))
    return { fresh, invalid, notInTeam }
  }

  // Turns what is typed into tags. Invalid/out-of-team addresses stay in
  // the field with an error; valid ones become tags.
  const commitInput = () => {
    const { fresh, invalid, notInTeam } = pendingFromInput()
    if (fresh.length > 0) setRecipients((list) => [...list, ...fresh])
    if (fresh.length > 0) setConfirmedResend(false)
    if (notInTeam.length > 0) {
      setInput([...notInTeam, ...invalid].join(', '))
      setInputError(`Vous ne pouvez partager qu'avec les membres de votre équipe. Seul(e) ${myTeam?.chef?.contactName || myTeam?.chef?.email} peut ajouter quelqu'un de nouveau : ${notInTeam.join(', ')}`)
    } else if (invalid.length > 0) {
      setInput(invalid.join(', '))
      setInputError(`Adresse email invalide : ${invalid.join(', ')}`)
    } else {
      setInput('')
      setInputError('')
    }
  }

  const updateRecipient = (email: string, patch: Partial<Recipient>) => {
    setRecipients((list) => list.map((r) => (r.email === email ? { ...r, ...patch } : r)))
    setConfirmedResend(false)
  }

  const removeRecipient = (email: string) => {
    setRecipients((list) => list.filter((r) => r.email !== email))
    setConfirmedResend(false)
  }

  const handleSubmit = async () => {
    // Whatever is still in the field is sent too, so clicking Envoyer without
    // pressing Entrée doesn't drop the address.
    const { fresh, invalid, notInTeam } = pendingFromInput()
    if (notInTeam.length > 0) {
      setInputError(`Vous ne pouvez partager qu'avec les membres de votre équipe. Seul(e) ${myTeam?.chef?.contactName || myTeam?.chef?.email} peut ajouter quelqu'un de nouveau : ${notInTeam.join(', ')}`)
      return
    }
    if (invalid.length > 0) {
      setInputError(`Adresse email invalide : ${invalid.join(', ')}`)
      return
    }
    const toSend = [...recipients, ...fresh]
    if (toSend.length === 0) {
      setInputError('Ajoutez au moins une adresse email')
      return
    }
    // The team-size cap is the chef's to manage — a collaborateur can only
    // pick from the existing team anyway (enforced above), which by
    // definition never exceeds it.
    if (!isCollaborator && newCollaborators(toSend).length > seatsLeft) {
      setInputError(`Limite atteinte : ${MAX_COLLABORATORS} collaborateurs maximum par équipe (${seatsLeft} place${seatsLeft > 1 ? 's' : ''} restante${seatsLeft > 1 ? 's' : ''}).`)
      return
    }

    if (sharedOnThisCase(toSend).length > 0 && !confirmedResend) {
      setRecipients(toSend)
      setInput('')
      setConfirmedResend(true)
      return
    }

    const sent: string[] = []
    const failed: { recipient: Recipient; reason: string }[] = []
    for (const r of toSend) {
      try {
        await shareBlotCase({
          caseId,
          email: r.email,
          role: r.role,
          jobTitle: r.role === 'editor' ? resolveJobTitle(r) || undefined : undefined,
          message: message.trim() || undefined,
        })
        sent.push(r.email)
      } catch (e: any) {
        // Surface the BE's specific message when present (e.g. "Cannot share
        // with yourself", "Case not found or not shareable by you").
        failed.push({ recipient: r, reason: e?.response?.data?.message || e?.message || 'Erreur' })
      }
    }

    if (sent.length > 0) {
      toast({
        title: t('chu.collaboration.success'),
        description: `Invitation envoyée à ${sent.join(', ')}`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      })
    }
    if (failed.length > 0) {
      toast({
        title: t('chu.collaboration.error'),
        description: failed.map((f) => `${f.recipient.email} : ${f.reason}`).join('\n'),
        status: 'error',
        duration: 6000,
        isClosable: true,
      })
      // Keep only the recipients that failed, so they can be fixed and resent.
      setRecipients(failed.map((f) => f.recipient))
      setInput('')
      setConfirmedResend(false)
      return
    }

    setRecipients([])
    setInput('')
    setInputError('')
    setMessage('')
    setConfirmedResend(false)
    onClose()
  }

  const hasCollaborator = recipients.some((r) => r.role === 'editor')
  const teamEmails = new Set((myCollaborators ?? []).map((c) => c.email.toLowerCase()))
  const seatsLeft = Math.max(0, MAX_COLLABORATORS - teamEmails.size)
  const newCollaborators = (list: Recipient[]) =>
    list.filter((r) => r.role === 'editor' && !teamEmails.has(r.email))

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md" isCentered>
      <ModalOverlay backdropFilter="blur(4px)" />
      <ModalContent borderRadius="2xl" overflow="hidden">
        <ModalHeader
          bgGradient="linear(to-r, blue.500, teal.400)"
          color="white"
          py={4}
        >
          <HStack spacing={2}>
            <Icon as={LuShare2} boxSize={5} />
            <Text>{t('chu.collaboration.shareCase')}</Text>
          </HStack>
          {caseName && (
            <Text fontSize="sm" fontWeight="normal" mt={1} opacity={0.85}>
              {caseName}
            </Text>
          )}
        </ModalHeader>
        <ModalCloseButton color="white" top={3} />

        <ModalBody py={6}>
          <VStack spacing={4} align="stretch">

            <FormControl isInvalid={!!inputError}>
              <FormLabel fontSize="sm">{t('chu.collaboration.emailLabel')}</FormLabel>
              <HStack>
                <Input
                  type="text"
                  placeholder="prenom.nom@chu.fr — Entrée pour ajouter"
                  value={input}
                  onChange={(e) => { setInput(e.target.value); setInputError('') }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ',' || e.key === ';') {
                      e.preventDefault()
                      commitInput()
                    }
                  }}
                />
                <IconButton
                  aria-label="Ajouter"
                  icon={<Icon as={LuPlus} />}
                  onClick={commitInput}
                  isDisabled={!input.trim()}
                  variant="outline"
                />
              </HStack>
              {inputError && <FormErrorMessage>{inputError}</FormErrorMessage>}
            </FormControl>

            {recipients.length > 0 && (
              <VStack align="stretch" spacing={2}>
                {recipients.map((r) => {
                  const resolved = resolveJobTitle(r)
                  return (
                    <Box key={r.email} borderWidth="1px" borderColor="gray.200" borderRadius="lg" p={2.5}>
                      <HStack justify="space-between" mb={r.role === 'editor' ? 2 : 0}>
                        <Tag size="md" variant="subtle" colorScheme={r.role === 'editor' ? 'blue' : 'gray'} borderRadius="full">
                          <TagLabel>{r.email}</TagLabel>
                          <TagCloseButton onClick={() => removeRecipient(r.email)} />
                        </Tag>
                        <Select
                          size="xs"
                          w="auto"
                          value={r.role}
                          onChange={(e) => updateRecipient(r.email, { role: e.target.value as Role })}
                        >
                          <option value="viewer">{t('chu.collaboration.roleViewer')}</option>
                          <option value="editor">{t('chu.collaboration.roleEditor')}</option>
                        </Select>
                      </HStack>
                      {r.role === 'editor' && (
                        r.locked ? (
                          <Text fontSize="xs" color="gray.500">
                            Rôle au laboratoire : <Text as="span" fontWeight="600" color="gray.700">{resolved}</Text>
                          </Text>
                        ) : (
                          <>
                            <Select
                              size="sm"
                              value={r.preset}
                              onChange={(e) => updateRecipient(r.email, { preset: e.target.value })}
                            >
                              {JOB_TITLE_PRESETS.map((label) => (
                                <option key={label} value={label}>{label}</option>
                              ))}
                              <option value={JOB_TITLE_OTHER}>{JOB_TITLE_OTHER}</option>
                            </Select>
                            {r.preset === JOB_TITLE_OTHER && (
                              <Input
                                mt={2}
                                size="sm"
                                placeholder="Précisez le rôle"
                                value={r.other}
                                onChange={(e) => updateRecipient(r.email, { other: e.target.value })}
                              />
                            )}
                            {!resolved && (
                              <Text fontSize="2xs" color="orange.500" mt={1}>Indiquez son rôle au laboratoire</Text>
                            )}
                          </>
                        )
                      )}
                    </Box>
                  )
                })}
              </VStack>
            )}

            {isCollaborator && teammates.length > 0 && (
              <Box>
                <Text fontSize="2xs" color="gray.500" mb={1.5}>
                  Partager avec un membre de votre équipe :
                </Text>
                <Wrap spacing={1.5}>
                  {teammates.map((m) => (
                    <WrapItem key={m.email}>
                      <Tag
                        size="sm"
                        variant="subtle"
                        colorScheme="purple"
                        cursor="pointer"
                        borderRadius="full"
                        _hover={{ bg: 'purple.100' }}
                        onClick={() => addRecipient({ email: m.email, ...knownRecipientDefaults(m.email) })}
                      >
                        {m.email}
                      </Tag>
                    </WrapItem>
                  ))}
                </Wrap>
                <Text fontSize="2xs" color="gray.400" mt={1.5}>
                  Seul(e) {myTeam?.chef?.contactName || myTeam?.chef?.email} peut ajouter quelqu'un de nouveau à l'équipe.
                </Text>
              </Box>
            )}

            {reusableCollaborators.length > 0 && (
              <Box>
                <Text fontSize="2xs" color="gray.500" mb={1.5}>
                  Réutiliser un collaborateur actif :
                </Text>
                <Wrap spacing={1.5}>
                  {reusableCollaborators.map((c) => (
                    <WrapItem key={c.email}>
                      <Tag
                        size="sm"
                        variant="subtle"
                        colorScheme="blue"
                        cursor="pointer"
                        borderRadius="full"
                        _hover={{ bg: 'blue.100' }}
                        onClick={() => addRecipient({ email: c.email, role: 'editor', ...presetFromTitle(c.jobTitle), locked: true })}
                      >
                        {c.email}
                      </Tag>
                    </WrapItem>
                  ))}
                </Wrap>
              </Box>
            )}

            {alreadyShared.length > 0 && (
              <Alert status="warning" borderRadius="lg" fontSize="sm" alignItems="flex-start">
                <AlertIcon />
                <Text>
                  {alreadyShared.map((x) => x.recipient.email).join(', ')} {alreadyShared.length > 1 ? 'ont déjà accès à ce cas' : 'a déjà accès à ce cas'}.
                  Renvoyer une invitation générera un <strong>nouveau lien</strong> et invalidera l'ancien.
                </Text>
              </Alert>
            )}

            <FormControl>
              <FormLabel fontSize="sm">{t('chu.collaboration.messageLabel')}</FormLabel>
              <Textarea
                placeholder={t('chu.collaboration.messagePlaceholder')}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={3}
                resize="none"
                fontSize="sm"
              />
            </FormControl>

            {!isCollaborator && (
              <Text fontSize="xs" color={seatsLeft === 0 ? 'orange.600' : 'gray.500'}>
                Équipe : {teamEmails.size} / {MAX_COLLABORATORS} collaborateurs
                {seatsLeft === 0 ? ' — limite atteinte' : ` — ${seatsLeft} place${seatsLeft > 1 ? 's' : ''} disponible${seatsLeft > 1 ? 's' : ''}`}
              </Text>
            )}

            {hasCollaborator && (
              <Text fontSize="xs" color="gray.500">
                Un collaborateur crée son compte pour accéder au cas. Vous restez responsable du cas en tant qu'invitant(e).
              </Text>
            )}

            {/* Who's already been invited on this case — so re-opening the
                modal doesn't require guessing from memory who has access. */}
            {loadingShares ? (
              <Center py={2}><Spinner size="sm" color="blue.400" /></Center>
            ) : existingShares && existingShares.length > 0 && (
              <>
                <Divider />
                <VStack spacing={2} align="stretch">
                  <HStack spacing={2}>
                    <Icon as={LuUsers} boxSize={4} color="gray.500" />
                    <Text fontSize="xs" fontWeight="bold" color="gray.600" textTransform="uppercase" letterSpacing="wide">
                      Déjà invités ({existingShares.length})
                    </Text>
                  </HStack>
                  <VStack spacing={1.5} align="stretch" maxH="140px" overflowY="auto">
                    {existingShares.map((s) => {
                      const isActive = s.status === 'accepted'
                      // Quick re-invite: an active collaborateur is added as a
                      // recipient with their last role and job title.
                      const handleQuickFill = () => {
                        if (!isActive) return
                        addRecipient({ email: s.receiverEmail, role: s.role, ...presetFromTitle(s.jobTitle), locked: s.role === 'editor' })
                      }
                      return (
                        <Tooltip
                          key={s.id}
                          label={isActive ? 'Cliquer pour ajouter à la liste' : undefined}
                          isDisabled={!isActive}
                          placement="left"
                          hasArrow
                        >
                          <HStack
                            justify="space-between"
                            fontSize="xs"
                            px={1}
                            py={0.5}
                            borderRadius="md"
                            cursor={isActive ? 'pointer' : 'default'}
                            _hover={isActive ? { bg: 'blue.50' } : undefined}
                            onClick={handleQuickFill}
                          >
                            <VStack spacing={0} align="start">
                              <Text color="gray.700">{s.receiverEmail}</Text>
                              {s.jobTitle && <Text color="gray.400" fontSize="2xs">{s.jobTitle}</Text>}
                            </VStack>
                            <HStack spacing={1.5}>
                              <Badge colorScheme={s.role === 'editor' ? 'blue' : 'gray'} fontSize="2xs">
                                {s.role === 'editor' ? 'Collaborateur' : 'Lecteur'}
                              </Badge>
                              <Badge
                                colorScheme={isActive ? 'green' : 'orange'}
                                variant="subtle"
                                fontSize="2xs"
                              >
                                {isActive ? 'Actif' : 'En attente'}
                              </Badge>
                            </HStack>
                          </HStack>
                        </Tooltip>
                      )
                    })}
                  </VStack>
                </VStack>
              </>
            )}

          </VStack>
        </ModalBody>

        <ModalFooter borderTop="1px solid" borderColor="gray.100" gap={2}>
          <Button
            variant="ghost"
            onClick={onClose}
            size="sm"
          >
            {t('chu.collaboration.cancel')}
          </Button>
          <Button
            colorScheme={alreadyShared.length > 0 && confirmedResend ? 'orange' : 'blue'}
            leftIcon={<Icon as={LuUserPlus} />}
            onClick={handleSubmit}
            isLoading={isPending}
            loadingText={t('chu.collaboration.sending')}
            size="sm"
            isDisabled={recipients.length === 0 && !input.trim()}
          >
            {alreadyShared.length > 0 && confirmedResend
              ? 'Confirmer le renvoi'
              : recipients.length > 1
                ? `Envoyer ${recipients.length} invitations`
                : t('chu.collaboration.send')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
